import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { WorkflowRules } from '../components/WorkflowRules';
import { ErrorBanner, Field, Loading, Modal, PageHead } from '../components/ui';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import { useResource } from '../lib/hooks';
import type {
  ManualDocument,
  ManualSection,
  ManualSectionDetail,
  ManualSearchHit,
} from '../lib/types';

/** The droppable id for a container, so a drop on empty space still lands. */
const containerId = (parentId: number | null) => `list:${parentId ?? 'root'}`;

/** Reads one back. Returns `undefined` when the id is a section, not a list. */
function parentFromContainerId(id: string): number | null | undefined {
  if (!id.startsWith('list:')) return undefined;
  const rest = id.slice(5);
  return rest === 'root' ? null : Number(rest);
}

/**
 * The contents, draggable.
 *
 * THREE LEVELS, not the two this started with. The manual was a flat pair —
 * ten chapters and their lettered sections — right up until 1C needed a
 * reference list (the workflow rule catalogue) that had no business being
 * pasted into the procedure above it. Nesting is built by walking the flat
 * list rather than recursing without a bound: the numbering only goes this
 * deep, and a fourth level would want a different nav, not another `.filter`.
 *
 * EVERY LIST IS ITS OWN SORTABLE CONTAINER — the chapters, and each section's
 * children — and dropping into a different one re-parents. That is what makes
 * "drag a section to another chapter" the same gesture as "reorder it where it
 * is", rather than a separate move dialog. The API takes both as one call; see
 * PUT /manual/order.
 *
 * SORTING IS SETTLED ON DROP, not while the pointer moves across containers.
 * dnd-kit can reflow between lists live, and it wants the active item's
 * container tracked through `onDragOver` with the list rebuilt on every frame.
 * For a nav of 88 rows that is a lot of moving parts to hold correct for the
 * sake of a preview, and the thing being dragged follows the cursor either
 * way. Within one list the live sort is dnd-kit's own and costs nothing.
 *
 * The order is applied LOCALLY FIRST and put back on failure. A row that
 * stays where it was dropped and is somewhere else on the next load is worse
 * than one that visibly refuses to move — same rule as the scope task library.
 */
function ContentsNav({
  sections,
  activeSlug,
  onSelect,
  onOptimistic,
  onRevert,
  onError,
}: {
  sections: ManualSection[];
  activeSlug: string | null;
  onSelect: (slug: string) => void;
  onOptimistic: (next: ManualSection[]) => void;
  onRevert: () => void;
  onError: (message: string | null) => void;
}) {
  const sensors = useSensors(
    /* The rows are links first and draggable second, so a drag has to be
       deliberate. The grip is a button of its own for the same reason: with
       the listeners on the link, the keyboard sensor would take space and
       enter and there would be no way to open a section without a mouse. */
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const childrenOf = (id: number | null) => sections.filter((s) => s.ParentSectionId === id);
  const chapters = childrenOf(null);

  async function handleDragEnd(event: DragEndEvent) {
    const activeId = Number(event.active.id);
    if (!event.over) return;
    const overRaw = String(event.over.id);
    if (overRaw === String(activeId)) return;

    const active = sections.find((s) => s.SectionId === activeId);
    if (!active) return;

    /* Dropped on a list's empty space, or on one of its rows. */
    const overContainer = parentFromContainerId(overRaw);
    const overSection =
      overContainer === undefined
        ? sections.find((s) => s.SectionId === Number(overRaw))
        : undefined;
    if (overContainer === undefined && !overSection) return;
    const parentId = overSection ? overSection.ParentSectionId : (overContainer ?? null);

    /* A section dropped into its own subtree would leave a cycle with no root
       to render from, so the whole branch would disappear from the nav at
       once. The API refuses it too — this is only so the row does not jump
       and come back. */
    let cursor: number | null = parentId;
    let guard = 0;
    while (cursor !== null && guard++ < 10) {
      if (cursor === activeId) {
        onError('A section cannot be dropped inside itself.');
        return;
      }
      cursor = sections.find((s) => s.SectionId === cursor)?.ParentSectionId ?? null;
    }

    const list = childrenOf(parentId);
    let ordered: ManualSection[];
    if (active.ParentSectionId === parentId) {
      const from = list.findIndex((s) => s.SectionId === activeId);
      const to = overSection
        ? list.findIndex((s) => s.SectionId === overSection.SectionId)
        : list.length - 1;
      if (from < 0 || to < 0 || from === to) return;
      ordered = arrayMove(list, from, to);
    } else {
      const at = overSection
        ? Math.max(
            0,
            list.findIndex((s) => s.SectionId === overSection.SectionId),
          )
        : list.length;
      ordered = [...list];
      ordered.splice(at, 0, { ...active, ParentSectionId: parentId });
    }

    /* Rebuilt by container rather than spliced in place: the tree is read by
       filtering on ParentSectionId, so only the order WITHIN each container
       matters and the containers may sit anywhere in the flat array. */
    const others = sections.filter(
      (s) => s.ParentSectionId !== parentId && s.SectionId !== activeId,
    );
    const next = [
      ...others,
      ...ordered.map((s) => (s.SectionId === activeId ? { ...s, ParentSectionId: parentId } : s)),
    ];

    onOptimistic(next);
    onError(null);
    try {
      await api.put('/manual/order', {
        parentSectionId: parentId,
        sectionIds: ordered.map((s) => s.SectionId),
      });
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save the new order');
      onRevert();
    }
  }

  return (
    <nav className="manual-contents" aria-label="Contents">
      <div className="eyebrow mb-4">Contents</div>
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
        <DropList parentId={null} items={chapters}>
          {chapters.map((chapter) => (
            <div key={chapter.SectionId} className="manual-chapter">
              <SortableRow
                section={chapter}
                level={1}
                activeSlug={activeSlug}
                onSelect={onSelect}
              />
              {/* Reserved while empty: a chapter with nothing under it has no
                  row to aim at, and the only other way to fill it would be to
                  add a section from inside it. The section lists below get no
                  such strip — seventy of them, each eighteen pixels of drop
                  target nobody asked for, turned "drop next to this row" into
                  "drop inside whichever neighbour the pointer was closest to". */}
              <DropList
                parentId={chapter.SectionId}
                items={childrenOf(chapter.SectionId)}
                reserveWhenEmpty
              >
                {childrenOf(chapter.SectionId).map((child) => (
                  <div key={child.SectionId}>
                    <SortableRow
                      section={child}
                      level={2}
                      activeSlug={activeSlug}
                      onSelect={onSelect}
                    />
                    <DropList parentId={child.SectionId} items={childrenOf(child.SectionId)}>
                      {childrenOf(child.SectionId).map((leaf) => (
                        <SortableRow
                          key={leaf.SectionId}
                          section={leaf}
                          level={3}
                          activeSlug={activeSlug}
                          onSelect={onSelect}
                        />
                      ))}
                    </DropList>
                  </div>
                ))}
              </DropList>
            </div>
          ))}
        </DropList>
      </DndContext>
    </nav>
  );
}

/**
 * One list of siblings: sortable within itself, and a drop target as a whole.
 *
 * The droppable wrapper is what makes an EMPTY chapter reachable. Without it
 * there is no row to aim at, so a chapter with nothing under it could never
 * receive a section and the only way to fill it would be to add one from
 * inside it.
 */
function DropList({
  parentId,
  items,
  reserveWhenEmpty = false,
  children,
}: {
  parentId: number | null;
  items: ManualSection[];
  /** Keep a strip of height while empty, so there is something to aim at. */
  reserveWhenEmpty?: boolean;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: containerId(parentId) });
  const empty = items.length === 0;
  return (
    <SortableContext
      items={items.map((s) => String(s.SectionId))}
      strategy={verticalListSortingStrategy}
    >
      <div
        ref={setNodeRef}
        className={[
          'manual-droplist',
          isOver ? 'over' : '',
          empty && reserveWhenEmpty ? 'reserved' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </div>
    </SortableContext>
  );
}

/** A contents row, draggable by its grip. The same ⠿ as the scope tasks. */
function SortableRow({
  section,
  level,
  activeSlug,
  onSelect,
}: {
  section: ManualSection;
  level: 1 | 2 | 3;
  activeSlug: string | null;
  onSelect: (slug: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(section.SectionId) });

  const isActive = activeSlug === section.Slug;
  const rowClass = [
    'manual-row',
    level === 3 ? 'nested' : '',
    level === 3 && isActive ? 'nested-active' : '',
    isDragging ? 'dragging' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={setNodeRef}
      className={rowClass}
      style={
        {
          transform: CSS.Transform.toString(transform),
          transition,
          ...(isDragging ? { position: 'relative', zIndex: 2, opacity: 0.65 } : null),
        } as CSSProperties
      }
    >
      <button
        type="button"
        className="manual-grip"
        ref={setActivatorNodeRef}
        title="Drag to reorder, or into another chapter"
        aria-label={`Move ${section.Title}`}
        {...attributes}
        {...listeners}
      >
        ⠿
      </button>
      <button
        type="button"
        className={`manual-link${level === 1 ? ' chapter' : ''}${isActive ? ' active' : ''}`}
        onClick={() => onSelect(section.Slug)}
      >
        <span className="mono xs">{section.Code}</span>
        <span className="grow">{section.Title}</span>
        {section.DocumentCount > 0 && (
          <span className="xs subtle mono">{section.DocumentCount}</span>
        )}
      </button>
    </div>
  );
}

/**
 * Sections whose content is a piece of the app rather than a piece of HTML.
 *
 * Keyed by slug rather than by code: the code is the manual's numbering and
 * gets renumbered, the slug is the stable handle and is what the API reads.
 */
const EMBEDS: Record<string, () => ReactNode> = {
  '1c1-workflow-rule-catalogue': () => <WorkflowRules />,
};

/**
 * The Operations Manual.
 *
 * Ported from the Flask app it replaces — same sections, same numbering, same
 * version history, wearing this app's design system instead of its own. The
 * evidence documents came across too and are served from here, so nothing
 * points back at the old site.
 *
 * Editing writes a new version rather than overwriting; the history is what
 * makes a manual trustworthy, and "what did this say in March" has to stay
 * answerable.
 */
export function OperationsManualPage() {
  const contents = useResource<ManualSection[]>('/manual');
  /**
   * The open section lives in the URL, not in state.
   *
   * It was `useState` until an SOP in Account Management needed to link to a
   * procedure rather than to a copy of it: a section nobody can address is a
   * section nobody can link to, and the only shareable thing on this page was
   * the page. The slug is already the stable handle — the code gets
   * renumbered, the slug does not — so it is what the path carries.
   *
   * `replace` on selection, so clicking down the contents does not fill the
   * back button with every section somebody glanced at on the way.
   */
  const { slug: slugParam } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const slug = slugParam ?? null;
  const setSlug = (next: string | null) =>
    navigate(next === null ? '/operations-manual' : `/operations-manual/${next}`, {
      replace: true,
    });
  /* The section a new one is being added under. `null` is not "closed" here —
     it is either "a new chapter", which has no parent, or "a section whose
     chapter has not been picked yet" — so the modal is driven by its own flag,
     and what is being made is carried separately. */
  const [addingUnder, setAddingUnder] = useState<ManualSection | null>(null);
  const [addingKind, setAddingKind] = useState<'chapter' | 'section'>('chapter');
  const [adding, setAdding] = useState(false);
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  /* Reordering is the only thing the page itself can fail at — everything else
     is inside a section view, which carries its own banner. */
  const [navError, setNavError] = useState<string | null>(null);

  const search = useResource<ManualSearchHit[]>(
    query.trim().length >= 2 ? `/manual/search?q=${encodeURIComponent(query.trim())}` : null,
  );

  /**
   * How deep each section sits, by slug.
   *
   * Read by the section view to decide whether it may still take a child. The
   * detail endpoint knows a section's parent but not its grandparent, and the
   * contents list is already here with the whole shape of the tree in it — so
   * the depth is computed once here rather than asked for per section.
   */
  const depthBySlug = useMemo(() => {
    const all = contents.data ?? [];
    const byId = new Map(all.map((x) => [x.SectionId, x]));
    const out = new Map<string, number>();
    for (const section of all) {
      let depth = 1;
      let cursor = section.ParentSectionId;
      /* Bounded rather than trusting the data: a cycle would otherwise hang
         the render, and the API caps this at three anyway. */
      while (cursor !== null && depth < 6) {
        depth += 1;
        cursor = byId.get(cursor)?.ParentSectionId ?? null;
      }
      out.set(section.Slug, depth);
    }
    return out;
  }, [contents.data]);

  if (contents.loading) return <Loading label="Loading the manual" />;
  if (contents.error) return <ErrorBanner message={contents.error} />;

  const sections = contents.data ?? [];

  return (
    <>
      <PageHead
        title="Operations Manual"
        lede="How we do things: the standard operating procedures, the configuration decisions and the evidence behind them. Every section keeps its history, so what it used to say is never lost."
        actions={
          <div className="row">
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                setQuery(term);
              }}
            >
              <input
                className="input"
                style={{ width: 220 }}
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Search the manual…"
                aria-label="Search the manual"
              />
              <button type="submit" className="btn btn-ghost">
                Search
              </button>
            </form>
            {/* Both from here. A section used to be addable only from inside
                its chapter, which meant finding the chapter first; the modal
                now asks which one, from a list of ten rather than the dropdown
                of all 88 sections that argument was really against.
                Sub-sections are still added from inside their section, where
                the parent is the page you are already looking at. */}
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setAddingUnder(null);
                setAddingKind('chapter');
                setAdding(true);
              }}
            >
              Add chapter
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setAddingUnder(null);
                setAddingKind('section');
                setAdding(true);
              }}
            >
              Add section
            </button>
          </div>
        }
      />

      {query.trim().length >= 2 && (
        <section className="card mb-4">
          <div className="card-head">
            <div className="grow">
              <span className="eyebrow">Search</span>
              <h2>
                {search.loading ? 'Searching…' : `${search.data?.length ?? 0} matches for “${query}”`}
              </h2>
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setQuery('');
                setTerm('');
              }}
            >
              Clear
            </button>
          </div>
          {(search.data?.length ?? 0) > 0 && (
            <div className="card-body stack" style={{ gap: 'var(--space-2)' }}>
              {search.data?.map((hit) => (
                <button
                  key={hit.SectionId}
                  type="button"
                  className="mini-row as-button"
                  onClick={() => {
                    setSlug(hit.Slug);
                    setQuery('');
                  }}
                >
                  <span className="mono xs muted" style={{ width: 44 }}>
                    {hit.Code}
                  </span>
                  <span className="grow strong small">{hit.Title}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {navError && <ErrorBanner message={navError} />}

      <div className="manual-layout">
        <ContentsNav
          sections={sections}
          activeSlug={slug}
          onSelect={setSlug}
          onOptimistic={(next) => contents.set(() => next)}
          onRevert={() => contents.reload()}
          onError={setNavError}
        />

        <div className="manual-body">
          {slug === null ? (
            <div className="card">
              <div className="card-body">
                <span className="eyebrow">Start here</span>
                <h2 className="mt-2">{sections.length} sections</h2>
                <p className="muted mt-4">
                  Pick a section from the contents. Each one carries its procedure, the documents
                  that go with it, and every previous version.
                </p>
                <p className="small muted mt-4">
                  {sections.reduce((n, s) => n + s.DocumentCount, 0)} attached documents —
                  spreadsheets, diagrams and templates — moved across with the text.
                </p>
              </div>
            </div>
          ) : (
            <SectionView
              slug={slug}
              depth={depthBySlug.get(slug) ?? 1}
              onNavigate={setSlug}
              onStructureChanged={() => contents.reload()}
              onAddChild={(parent) => {
                setAddingUnder(parent);
                setAddingKind('section');
                setAdding(true);
              }}
            />
          )}
        </div>
      </div>

      {adding && (
        <SectionCreator
          kind={addingKind}
          parent={addingUnder}
          sections={sections}
          onClose={() => setAdding(false)}
          onCreated={(created) => {
            setAdding(false);
            contents.reload();
            /* Straight to the new section, which is empty and wants writing —
               leaving the reader on the parent hides the thing they just made. */
            setSlug(created.Slug);
          }}
        />
      )}
    </>
  );
}

function SectionView({
  slug,
  depth,
  onNavigate,
  onStructureChanged,
  onAddChild,
}: {
  slug: string;
  depth: number;
  onNavigate: (slug: string | null) => void;
  onStructureChanged: () => void;
  onAddChild: (parent: ManualSection) => void;
}) {
  const resource = useResource<ManualSectionDetail>(`/manual/sections/${slug}`);
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (resource.loading) return <Loading />;
  if (resource.error) return <ErrorBanner message={resource.error} />;
  const s = resource.data;
  if (!s) return null;

  return (
    <>
      {error && <ErrorBanner message={error} />}

      <article className="card">
        <div className="card-head">
          <div className="grow">
            {s.ParentTitle && (
              <button
                type="button"
                className="eyebrow manual-crumb"
                onClick={() => s.ParentSlug && onNavigate(s.ParentSlug)}
              >
                {s.ParentTitle}
              </button>
            )}
            <h2>
              {s.Code && <span className="mono manual-code">{s.Code}</span>}
              {s.Title}
            </h2>
            <p className="sub">
              Version {s.VersionNumber ?? 1} · updated {formatDate(s.UpdatedUtc)}
              {s.Author ? ` by ${s.Author}` : ''}
            </p>
          </div>
          <div className="row">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setShowHistory(true)}
            >
              History ({s.history.length})
            </button>
            {/* Three deep is the limit the API enforces and the contents nav
                renders — see the note on `tree` above. Past that the control
                goes away rather than offering something that would be refused. */}
            {depth < 3 && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  onAddChild({
                    SectionId: s.SectionId,
                    Code: s.Code,
                    Title: s.Title,
                    Slug: s.Slug,
                    ParentSectionId: s.ParentSectionId,
                    SortOrder: 0,
                    VersionNumber: s.VersionNumber,
                    UpdatedUtc: s.UpdatedUtc,
                    Author: s.Author,
                    DocumentCount: s.documents.length,
                    VersionCount: s.history.length,
                  })
                }
              >
                {depth === 1 ? 'Add section' : 'Add sub-section'}
              </button>
            )}
            <PageUploader
              slug={s.Slug}
              onUploaded={() => {
                resource.reload();
                onStructureChanged();
              }}
            />
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>
              Edit
            </button>
            {/* Retires rather than deletes, and the API refuses while the
                section still has children or attachments. */}
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() => {
                if (
                  !window.confirm(
                    `Retire "${s.Title}"? It comes out of the contents. Its text and every previous version are kept.`,
                  )
                ) {
                  return;
                }
                setBusy(true);
                setError(null);
                void api
                  .del(`/manual/sections/${s.Slug}`)
                  .then(() => {
                    onStructureChanged();
                    /* Back to the parent, or to the start page for a chapter —
                       the section just retired is no longer in the contents.
                       A chapter used to reload the whole window to get there,
                       because nothing but the page itself was addressable;
                       since the slug is in the path it is just a navigation. */
                    onNavigate(s.ParentSlug ?? null);
                  })
                  .catch((err: unknown) => {
                    setError(err instanceof Error ? err.message : 'Could not retire');
                    setBusy(false);
                  });
              }}
            >
              Retire
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* The manual's own HTML, from the source app. It is authored
              internally by editors who already have write access to this
              content, so rendering it is not a new trust boundary. */}
          <ManualBody html={s.BodyHtml ?? ''} />

          {/* A few sections are not text. 1C.1 is a catalogue of 87 workflow
              rules that has to be searched and filtered — so it is rendered by
              the app rather than pasted in as more HTML an editor would have to
              maintain by hand. The section keeps its own prose above; this is
              what sits under it. */}
          {EMBEDS[s.Slug]?.()}
        </div>

        {/* Always rendered now, even at nothing attached — the uploader lives
            here, and a section with no documents is exactly the one somebody
            has arrived to attach a file to. */}
        <div className="card-head" style={{ borderTop: '1px solid var(--border-subtle)' }}>
          <div className="grow">
            <span className="eyebrow">Evidence</span>
            <h3>
              {s.documents.length === 0
                ? 'No documents'
                : `${s.documents.length} ${s.documents.length === 1 ? 'document' : 'documents'}`}
            </h3>
          </div>
          <DocumentUploader slug={s.Slug} onUploaded={() => {
            resource.reload();
            onStructureChanged();
          }} />
        </div>
        <div className="card-body stack" style={{ gap: 'var(--space-2)' }}>
          {s.documents.length === 0 && (
            <p className="small muted">
              Attach the editable source behind a diagram — the .drawio or .vsdx file — so the
              next person to change this procedure can open it rather than redraw it. Spreadsheets
              and templates go here too.
            </p>
          )}
          {s.documents.map((d) => (
            <div key={d.DocumentId} className="mini-row">
              <span className="doc-ext mono xs">{(d.Extension ?? '').replace('.', '') || '?'}</span>
              <button
                type="button"
                className="grow small strong doc-name"
                onClick={() => {
                  setError(null);
                  void openDocument(d).catch((err: unknown) =>
                    setError(err instanceof Error ? err.message : 'Could not open that document'),
                  );
                }}
              >
                {d.Filename}
              </button>
              {d.UploadedBy && <span className="xs muted">{d.UploadedBy}</span>}
              <span className="xs muted mono">{fileSize(d.SizeBytes)}</span>
              {/* Imported documents ship with the code and the API refuses to
                  remove them, so the control is not offered for those. */}
              {d.IsUploaded && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    if (!window.confirm(`Remove "${d.Filename}" from this section?`)) return;
                    setError(null);
                    void api
                      .del(`/manual/documents/${d.DocumentId}`)
                      .then(() => {
                        resource.reload();
                        onStructureChanged();
                      })
                      .catch((err: unknown) =>
                        setError(err instanceof Error ? err.message : 'Could not remove'),
                      );
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      </article>

      {editing && (
        <SectionEditor
          section={s}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            resource.reload();
          }}
        />
      )}

      {showHistory && (
        <Modal
          title={`History — ${s.Title}`}
          onClose={() => setShowHistory(false)}
          footer={
            <button type="button" className="btn btn-ghost" onClick={() => setShowHistory(false)}>
              Close
            </button>
          }
        >
          <p className="xs muted">
            Every edit writes a new version. Restoring an old one writes it forward rather than
            deleting what came after, so the record stays complete.
          </p>
          <div className="stack" style={{ gap: 'var(--space-2)' }}>
            {s.history.map((v) => (
              <div key={v.VersionId} className="mini-row">
                <span className="mono xs" style={{ width: 32 }}>
                  v{v.VersionNumber}
                </span>
                <div className="grow">
                  <div className="small strong">
                    {v.Title}
                    {v.IsCurrent && <span className="badge badge-good ml-2">Current</span>}
                  </div>
                  <div className="xs muted">
                    {formatDate(v.CreatedUtc)}
                    {v.Author ? ` · ${v.Author}` : ''}
                    {v.Note ? ` · ${v.Note}` : ''}
                  </div>
                </div>
                {!v.IsCurrent && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() =>
                      void api
                        .post(`/manual/versions/${v.VersionId}/restore`)
                        .then(() => {
                          setShowHistory(false);
                          resource.reload();
                        })
                        .catch((err: unknown) =>
                          setError(err instanceof Error ? err.message : 'Could not restore'),
                        )
                    }
                  >
                    Restore
                  </button>
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
}

function SectionEditor({
  section,
  onClose,
  onSaved,
}: {
  section: ManualSectionDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(section.Title);
  const [code, setCode] = useState(section.Code ?? '');
  const [bodyHtml, setBodyHtml] = useState(section.BodyHtml ?? '');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/manual/sections/${section.Slug}`, {
        title: title.trim(),
        code: code.trim() || null,
        bodyHtml,
        note: note.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Edit ${section.Code ?? ''} ${section.Title}`.trim()}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !title.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save as new version'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <div className="grid grid-2">
        <Field label="Number" hint="The manual's own numbering — 4, 4B, 9G.">
          <input
            className="input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. 4B"
          />
        </Field>
        <Field label="Title">
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
      </div>

      <Field label="Content" hint="HTML, as the manual has always stored it.">
        <textarea
          className="textarea"
          style={{ minHeight: 320, fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}
          value={bodyHtml}
          onChange={(e) => setBodyHtml(e.target.value)}
        />
      </Field>

      <Field label="What changed" hint="The most-skipped and most-valuable field here.">
        <input
          className="input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Updated the P1 response target to 15 minutes"
        />
      </Field>
    </Modal>
  );
}

/**
 * The manual's own HTML, plus whatever it takes to make it visible.
 *
 * Still dangerouslySetInnerHTML — the content is authored internally by people
 * who already have write access to it, so rendering it is not a new trust
 * boundary and never was.
 *
 * WHAT IS NEW IS THE DIAGRAM CASE. innerHTML does not execute a <script> tag,
 * by design and in every browser. A page uploaded from a .drawio.html export
 * therefore lands with its viewer script present in the markup and never run,
 * and the reader gets a blank space where the flowchart should be — which looks
 * exactly like a broken upload.
 *
 * So when the body contains a draw.io container, the viewer is loaded once for
 * the whole app and asked to scan again. GraphViewer.processElements() is
 * draw.io's own supported way to render containers that arrived after its
 * script did; without it the viewer only ever sees the DOM as it was at load.
 */
function ManualBody({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!html.includes('data-mxgraph=')) return;

    /* Loaded lazily and once. Every manual page would otherwise pay for a
       third-party script that almost none of them need. The CSP in server.ts
       allows exactly this host — see the note there. */
    const SRC = 'https://app.diagrams.net/js/viewer-static.min.js';
    type Viewer = { processElements?: () => void };
    const draw = () => (window as unknown as { GraphViewer?: Viewer }).GraphViewer?.processElements?.();

    if ((window as unknown as { GraphViewer?: Viewer }).GraphViewer) {
      draw();
      return;
    }
    /* Matched on OUR marker, not on the src. The uploaded body may itself
       contain a <script src> for the same viewer — inert, because innerHTML
       never executes one — and querying by src found that dead tag and waited
       on a load event it could never fire. */
    const existing = document.querySelector<HTMLScriptElement>('script[data-drawio-viewer]');
    if (existing) {
      existing.addEventListener('load', draw, { once: true });
      return;
    }
    const tag = document.createElement('script');
    tag.src = SRC;
    tag.async = true;
    tag.dataset.drawioViewer = 'true';
    tag.addEventListener('load', draw, { once: true });
    document.head.appendChild(tag);
  }, [html]);

  return (
    <div
      ref={ref}
      className="manual-content"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/**
 * Upload a whole HTML document as this section's page.
 *
 * Sent as text rather than base64: it is HTML, JSON carries it, and a reader of
 * the network tab can see what was uploaded. The server reduces it to a body
 * fragment and writes it as a new version, so this is an edit like any other —
 * revertible from History, and the previous text is never lost.
 */
function PageUploader({ slug, onUploaded }: { slug: string; onUploaded: () => void }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <label className="btn btn-ghost btn-sm" style={{ cursor: busy ? 'wait' : 'pointer' }}>
        {busy ? 'Uploading…' : 'Upload a page'}
        <input
          type="file"
          accept=".html,.htm"
          style={{ display: 'none' }}
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            /* Cleared straight away so the same file can be picked twice. */
            e.target.value = '';
            if (!file) return;
            if (
              !window.confirm(
                `Replace this section's content with "${file.name}"? The current text is kept as a previous version.`,
              )
            ) {
              return;
            }
            setBusy(true);
            setError(null);
            setNote(null);
            void file
              .text()
              .then((html) =>
                api.post<{ VersionNumber: number; Removed: string[]; HasDiagram: boolean }>(
                  '/manual/pages',
                  { sectionSlug: slug, filename: file.name, html, replace: true },
                ),
              )
              .then((r) => {
                setBusy(false);
                /* Said out loud rather than swallowed: a page whose script was
                   stripped may render nothing, and the reader deserves to know
                   why rather than concluding the upload failed. */
                const bits = [`Saved as version ${r.VersionNumber}.`];
                if (r.HasDiagram) bits.push('Diagram detected.');
                if (r.Removed.length > 0) bits.push(`Removed: ${r.Removed.join(', ')}.`);
                setNote(bits.join(' '));
                onUploaded();
              })
              .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not upload that page');
                setBusy(false);
              });
          }}
        />
      </label>
      {(note || error) && (
        <span className="xs" style={{ color: error ? 'var(--accent)' : 'var(--text-muted)' }}>
          {error ?? note}
        </span>
      )}
    </>
  );
}

/**
 * The number the next section of a chapter would take — "2H" after 2A…2G.
 *
 * Suggested, never imposed: it fills the placeholder so the common case is one
 * glance rather than a scroll up the contents, and typing 2D over it is how a
 * section gets inserted in the middle. Letters only go as far as Z here; past
 * that the suggestion is dropped rather than guessed, which no chapter is
 * close to.
 */
function nextCodeUnder(chapter: ManualSection, siblings: ManualSection[]): string | null {
  const chapterNumber = /^(\d{1,6})/.exec((chapter.Code ?? '').trim());
  if (!chapterNumber) return null;
  const used = siblings
    .map((s) => /^\d{1,6}([A-Z])$/.exec((s.Code ?? '').trim().toUpperCase())?.[1])
    .filter((letter): letter is string => Boolean(letter))
    .map((letter) => letter.charCodeAt(0));
  const next = used.length === 0 ? 65 : Math.max(...used) + 1;
  return next > 90 ? null : `${chapterNumber[1]}${String.fromCharCode(next)}`;
}

/**
 * Add a chapter, a section or a sub-section.
 *
 * The section is created empty and the caller navigates straight into it — the
 * body is written with the normal editor rather than in this modal, so that
 * creating and editing stay one skill instead of two.
 *
 * A SECTION ASKS WHICH CHAPTER. It used to take whichever section you happened
 * to be reading, so adding 2D meant navigating to chapter 2 first; the chapter
 * is now part of the form, pre-selected when you did come from one. A
 * sub-section still takes the section it was opened from — that one is the
 * page in front of you, and a dropdown of all 88 would be the thing the
 * original note against a picker was really about.
 *
 * THE NUMBER DECIDES WHERE IT LANDS, and the API does that placing: 2D goes
 * between 2C and 2E whether or not 2E was there first. Nothing here sorts.
 */
function SectionCreator({
  kind,
  parent,
  sections,
  onClose,
  onCreated,
}: {
  kind: 'chapter' | 'section';
  parent: ManualSection | null;
  sections: ManualSection[];
  onClose: () => void;
  onCreated: (created: { SectionId: number; Slug: string }) => void;
}) {
  const chapters = useMemo(() => sections.filter((s) => s.ParentSectionId === null), [sections]);
  /* A parent that is itself a section means a sub-section: that parent is
     fixed, and there is nothing to choose. A parent that is a chapter is just
     the starting value of the picker. */
  const fixedParent = parent && parent.ParentSectionId !== null ? parent : null;
  const [chapterId, setChapterId] = useState<number | null>(
    parent?.SectionId ?? chapters[0]?.SectionId ?? null,
  );

  const chapter = chapters.find((c) => c.SectionId === chapterId) ?? null;
  const parentSectionId =
    kind === 'chapter' ? null : (fixedParent?.SectionId ?? chapterId ?? null);

  const [title, setTitle] = useState('');
  const [code, setCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const suggestedCode =
    kind === 'chapter' || fixedParent || !chapter
      ? null
      : nextCodeUnder(
          chapter,
          sections.filter((s) => s.ParentSectionId === chapter.SectionId),
        );

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await api.post<{ SectionId: number; Slug: string }>('/manual/sections', {
        title: title.trim(),
        code: code.trim() || null,
        parentSectionId,
      });
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the section');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={
        kind === 'chapter'
          ? 'Add a chapter'
          : fixedParent
            ? `Add under ${fixedParent.Code ?? ''} ${fixedParent.Title}`.trim()
            : 'Add a section'
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !title.trim() || (kind === 'section' && parentSectionId === null)}
            onClick={() => void submit()}
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <p className="xs muted">
        It goes where its number puts it — 2D lands between 2C and 2E, and a chapter 3 between 2 and
        4. Without a number it goes to the end, and either way it can be dragged afterwards. The
        section starts empty; write it with Edit once it exists.
      </p>

      {kind === 'section' && !fixedParent && (
        <Field label="Chapter" hint="Which chapter it belongs under.">
          <select
            className="input"
            value={chapterId ?? ''}
            onChange={(e) => setChapterId(e.target.value ? Number(e.target.value) : null)}
          >
            {chapters.length === 0 && <option value="">No chapters yet</option>}
            {chapters.map((c) => (
              <option key={c.SectionId} value={c.SectionId}>
                {`${c.Code ?? ''} ${c.Title}`.trim()}
              </option>
            ))}
          </select>
        </Field>
      )}

      <div className="grid grid-2">
        <Field label="Number" hint="Optional. The manual's own numbering.">
          <input
            className="input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={
              kind === 'chapter' ? 'e.g. 11' : (suggestedCode ?? (fixedParent ? 'e.g. 1C2' : 'e.g. 4B'))
            }
          />
        </Field>
        <Field label="Title">
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Change Management"
          />
        </Field>
      </div>
    </Modal>
  );
}

/** Attach a file to a section. */
function DocumentUploader({ slug, onUploaded }: { slug: string; onUploaded: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="stack" style={{ gap: 'var(--space-1)', alignItems: 'flex-end' }}>
      <label className="btn btn-ghost btn-sm" style={{ cursor: busy ? 'wait' : 'pointer' }}>
        {busy ? 'Uploading…' : 'Attach a file'}
        <input
          type="file"
          style={{ display: 'none' }}
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            /* Cleared straight away so the same file can be picked twice —
               a change event does not fire for an identical value. */
            e.target.value = '';
            if (!file) return;
            setBusy(true);
            setError(null);
            void toBase64(file)
              .then((content) =>
                api.post('/manual/documents', { sectionSlug: slug, filename: file.name, content }),
              )
              .then(() => {
                setBusy(false);
                onUploaded();
              })
              .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not upload that file');
                setBusy(false);
              });
          }}
        />
      </label>
      {error && <span className="xs" style={{ color: 'var(--accent)' }}>{error}</span>}
    </div>
  );
}

/** Reads a file as base64, without the `data:...;base64,` prefix the API does
 *  not want. The same helper the job description library uses. */
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read "${file.name}".`));
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Open an attachment.
 *
 * Fetched with the API client rather than linked with a bare href, and that is
 * a fix rather than a preference. `/manual-assets` sits behind requireAuth,
 * which wants a bearer token — a plain navigation carries none, so those links
 * only ever worked locally, where the dev bypass waves them through. Going
 * through the API endpoint sends the token, and the endpoint redirects to the
 * static path for an imported document, so both kinds click the same way.
 */
async function openDocument(doc: ManualDocument): Promise<void> {
  const blob = await api.getBlob(`/manual/documents/${doc.DocumentId}/file`);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = doc.Filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  /* Revoked on a delay rather than immediately — Safari cancels the load if the
     object URL disappears before it has read it. */
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function fileSize(bytes: number): string {
  if (!bytes) return '—';
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}
